import { forwardRef, MutableRefObject, useLayoutEffect, useRef, useState } from 'react'

import { useGSAP } from '@/composables/useGSAP'

import ConversationBackgroundItem from './ConversationBackgroundItem'

export type ConversationBackgroundType =
  'neutral' | 'binary_1' | 'binary_2' | 'binary_3' | 'binary_4' | 'binary_5' | 'none'
export type ConversationChoiceType = {
  i18nKey: string
  ariaLabelI18nKey: string
  value: string
}
export type ConversationBackgroundProps = {
  type?: ConversationBackgroundType
  subject?: string
  choices?: ConversationChoiceType[]
  onChoose?: (choice: string) => void
  isLeaving?: boolean
}

const ConversationBackground = forwardRef<HTMLDivElement, ConversationBackgroundProps>(
  ({ type, subject, choices, onChoose, isLeaving }, rootRef) => {
    const [isReady, setIsReady] = useState(false)
    const [canCreateEngine, setCanCreateEngine] = useState(false)

    const [hoverIndex, setHoverIndex] = useState<number | null>(null)

    const { gsap } = useGSAP()

    useLayoutEffect(() => {
      if (type === 'none') return
      const ctx = gsap.context(() => {
        const root = (rootRef as MutableRefObject<HTMLDivElement>).current
        if (!root) return
        const timeline = gsap.timeline()

        const parts = root.querySelectorAll('.ConversationBackground__Part__Inner')
        timeline.add(() => setIsReady(false), 0)

        const rotations = [-22.5, -22.5, 22.5, -22.5, 22.5, 22.5, 22.5, -22.5]

        const stagger = 0.1
        timeline.addLabel('start', 0.3)
        timeline.fromTo(
          parts,
          {
            scale: 0.01,
            transformOrigin: 'center',
          },
          {
            scale: 1,
            duration: 0.6,
            ease: 'back.out(1.7)',
            stagger: (index) => (index < 3 ? index * stagger : (index - 3) * stagger),
          },
          'start'
        )
        timeline.fromTo(
          parts,
          {
            y: -60,
            rotate: (index) => rotations[index % rotations.length],
          },
          {
            x: 0,
            y: 0,
            rotate: 0,
            duration: 0.3,
            ease: 'power3.inOut',
            stagger: (index) =>
              index < 3 ? index * stagger : index < 5 ? (index - 3) * stagger : (index - 5) * stagger,
          },
          'start+=0.35'
        )

        timeline.add(() => setIsReady(true), 'start+=0.5')
        timeline.add(() => setCanCreateEngine(true))
      })

      return () => ctx.kill()
    }, [type, gsap, rootRef])

    useLayoutEffect(() => {
      if (type === 'none' || !isLeaving) return

      const ctx = gsap.context(() => {
        const root = (rootRef as MutableRefObject<HTMLDivElement>).current
        if (!root) return
        const timeline = gsap.timeline()

        const parts = root.querySelectorAll('.ConversationBackground__Part__Inner')
        timeline.add(() => setIsReady(false), 0)

        timeline.fromTo(
          parts,
          {
            scale: 1,
            transformOrigin: 'center',
          },
          {
            scale: 0.01,
            duration: 0.3,
            ease: 'back.in(1.2)',
            stagger: {
              amount: 0.2,
              from: 'end',
            },
          },
          0
        )

        timeline.fromTo(
          parts,
          {
            opacity: 1,
          },
          {
            opacity: 0,
            duration: 0.3,
            ease: 'power2.in',
            stagger: {
              amount: 0.2,
            },
          },
          0
        )
      })

      return () => ctx.kill()
    }, [isLeaving, gsap, type, rootRef])

    const getChoicePosition = (index: number) => {
      switch (index) {
        case 0:
          if (type === 'neutral') {
            return 'md:left-[83%] md:top-[81.2%]'
          } else {
            return 'md:left-[50%] md:top-[55%]'
          }
        case 1:
          if (type === 'neutral') {
            return 'md:left-[49.8%] md:top-[57%]'
          } else {
            return 'md:left-[80%] md:top-[74%]'
          }
        case 2:
          return 'md:left-[65.5%] md:top-[21.2%]'
      }
    }

    const svgRef = useRef<SVGSVGElement>(null)
    const hoverRef = useRef<SVGGElement[]>([])

    useLayoutEffect(() => {
      if (type === 'none') return
      const ctx = gsap.context(() => {
        hoverRef.current.forEach((item, index) => {
          if (hoverIndex === index) {
            gsap.to(item, {
              scale: 1.2,
              rotate: 12,
              transformOrigin: 'center',
              duration: 0.7,
              ease: 'power.out',
            })
          } else {
            gsap.to(item, {
              scale: 1,
              rotate: 0,
              transformOrigin: 'center',
              duration: 0.3,
              ease: 'power3.out',
            })
          }
        })
      })

      return () => ctx.kill()
    }, [type, gsap, hoverIndex])

    return (
      type !== 'none' && (
        <div
          ref={rootRef}
          className={`ConversationBackground order-1 md:order-0 md:absolute inset-0 pointer-events-none items-stretch text-secondary group ${
            isReady ? 'ready' : ''
          } ${hoverIndex !== null ? 'index-' + hoverIndex : ''} ${choices && choices?.length ? '' : 'hidden md:block'}`}
          aria-hidden={choices ? 'false' : 'true'}
        >
          <div className='w-full md:h-screen'>
            <svg
              aria-hidden='true'
              className='hidden md:block max-md:absolute max-md:bottom-0 w-full md:h-full z-1'
              data-type={type}
              viewBox='0 0 947 844'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              preserveAspectRatio='xMaxYMid meet'
              ref={svgRef}
            >
              {type === 'neutral' ? (
                <>
                  <g className='ConversationBackground__Part'>
                    <g className='ConversationBackground__Part__Inner'>
                      <path
                        d='M473.721 264.657C459.639 221.918 423.212 140.927 353.709 161.201C284.441 181.402 296.517 270.221 307.378 314.75C310.818 328.856 325.415 337.132 339.279 332.885L457.134 296.751C470.666 292.603 478.159 278.122 473.721 264.662V264.657Z'
                        fill='currentColor'
                      />
                    </g>
                  </g>
                  <g className='ConversationBackground__Part' data-interactive={2}>
                    <g className='ConversationBackground__Part__Inner'>
                      <g
                        ref={(ref) => {
                          hoverRef.current[2] = ref
                        }}
                      >
                        <path
                          d='M757.159 147.134C744.105 136.376 727.385 133.015 711.138 128.127C714.735 117.243 718.275 106.383 720.312 95.5718C724.885 61.0585 684.59 77.6272 646.165 88.4584C634.661 74.5418 624.313 58.9596 611.924 46.5945C590.502 27.4233 580.628 53.1517 570.696 84.0147C558.433 80.3936 545.998 77.2496 533.665 73.8539C533.234 73.7339 532.778 73.6343 532.336 73.5641C490.665 67.1181 524.136 128.247 529.435 154.818C515.421 165.401 502.281 177.207 488.865 188.6C488.522 188.89 488.183 189.209 487.878 189.537C468.101 210.692 494.626 220.508 521.262 228.014C507.175 272.907 493.253 301.212 589.843 265.82C590.458 265.571 591.073 265.334 591.685 265.109C629.655 304.698 646.52 358.599 672.064 275.823C684.057 279.549 696.123 282.415 708.407 281.97C713.887 281.771 718.535 277.67 719.036 272.208C720.985 250.841 709.785 231.304 705.23 210.321C723.512 194.358 746.409 181.811 759.252 161.414C762.173 156.774 761.403 150.623 757.17 147.134H757.159Z'
                          fill='currentColor'
                        />
                        <path
                          className={`fill-coral transition-opacity duration-333 ease-out-quad ${
                            choices?.length > 0 && isReady && (hoverIndex === null || hoverIndex === 2)
                              ? 'opacity-100'
                              : 'opacity-0'
                          }`}
                          d='M757.159 147.134C744.105 136.376 727.385 133.015 711.138 128.127C714.735 117.243 718.275 106.383 720.312 95.5718C724.885 61.0585 684.59 77.6272 646.165 88.4584C634.661 74.5418 624.313 58.9596 611.924 46.5945C590.502 27.4233 580.628 53.1517 570.696 84.0147C558.433 80.3936 545.998 77.2496 533.665 73.8539C533.234 73.7339 532.778 73.6343 532.336 73.5641C490.665 67.1181 524.136 128.247 529.435 154.818C515.421 165.401 502.281 177.207 488.865 188.6C488.522 188.89 488.183 189.209 487.878 189.537C468.101 210.692 494.626 220.508 521.262 228.014C507.175 272.907 493.253 301.212 589.843 265.82C590.458 265.571 591.073 265.334 591.685 265.109C629.655 304.698 646.52 358.599 672.064 275.823C684.057 279.549 696.123 282.415 708.407 281.97C713.887 281.771 718.535 277.67 719.036 272.208C720.985 250.841 709.785 231.304 705.23 210.321C723.512 194.358 746.409 181.811 759.252 161.414C762.173 156.774 761.403 150.623 757.17 147.134H757.159Z'
                        />
                      </g>
                    </g>
                  </g>
                  <g className='ConversationBackground__Part'>
                    <g className='ConversationBackground__Part__Inner'>
                      <path
                        d='M756.407 254.831C755.497 257.08 754.591 259.325 753.681 261.573C752.208 266.225 753.353 270.63 754.943 274.974C757.633 282.319 760.772 289.482 763.607 296.767C765.5 301.633 767.672 306.365 769.792 311.114C773.938 320.4 778.646 329.416 783.738 338.222C786.323 342.691 789.066 347.047 792.087 351.23C794.235 354.202 796.767 356.686 800.161 358.182C801.682 358.797 803.202 359.412 804.723 360.027C810.281 362.086 815.515 360.545 820.68 358.49C825.579 356.54 830.085 353.798 834.716 351.302C837.984 349.539 841.322 347.905 844.65 346.263C849.97 343.633 855.272 340.98 860.604 338.375C864.503 336.47 868.426 334.606 872.297 332.644C879.78 328.854 887.506 325.529 894.649 321.08C897.198 319.49 899.572 317.674 901.772 315.619C903.148 314.337 903.754 312.57 904.762 311.057C905.563 309.079 906.359 307.101 907.16 305.123C909.037 300.015 907.407 295.242 905.789 290.477C904.139 285.624 902.157 280.903 899.843 276.337C897.27 271.265 894.698 266.193 892.158 261.104C890.325 257.44 888.566 253.735 886.762 250.058C883.728 243.865 880.852 237.6 877.843 231.395C876.791 229.223 876.023 226.918 874.991 224.734C873.24 221.033 871.557 217.304 869.049 214.003C867.48 211.936 865.534 210.484 863.245 209.396C862.95 209.254 862.573 209.246 862.399 208.899C861.057 208.357 859.718 207.815 858.375 207.273C856.498 206.945 854.759 206.031 852.801 206.002C848.534 205.942 844.493 207.01 840.549 208.506C835.485 210.427 830.74 213.032 826.024 215.678C822.658 217.566 819.196 219.273 815.778 221.057C810.779 223.67 805.739 226.21 800.723 228.791C796.949 230.732 793.163 232.649 789.389 234.591C784.119 237.309 778.832 239.999 773.606 242.81C770.508 244.476 767.409 246.155 764.262 247.732C760.966 249.387 758.009 251.344 756.403 254.843L756.407 254.831Z'
                        fill='currentColor'
                      />
                    </g>
                  </g>
                  <g className='ConversationBackground__Part' data-interactive={1}>
                    <g className='ConversationBackground__Part__Inner'>
                      <g
                        ref={(ref) => {
                          hoverRef.current[1] = ref
                        }}
                      >
                        <path
                          d='M464.943 594.492C467.15 597.111 469.62 599.676 472.364 602.169C498.379 624.966 531.192 626.048 556.938 606.289C584.14 582.636 586.467 548.812 571.731 524.135C574.087 522.724 576.398 521.152 578.655 519.42C622.671 481.146 601.559 416.245 548.411 406.552C544.609 406.233 540.889 406.179 537.266 406.37C538.155 376.403 517.265 347.605 483.262 341.403C446.052 338.281 416.727 360.547 408.781 388.627C349.879 389.381 314.714 454.316 362.424 497.661C366.019 500.811 369.744 503.547 373.563 505.87C355.361 528.893 354.192 562.62 382.781 588.596C407.99 610.687 439.583 612.387 464.943 594.492Z'
                          fill='currentColor'
                        />
                        <path
                          className={`fill-mid-blue transition-opacity duration-333 ease-out-quad ${
                            choices?.length > 0 && isReady && (hoverIndex === null || hoverIndex === 1)
                              ? 'opacity-100'
                              : 'opacity-0'
                          }`}
                          d='M464.943 594.492C467.15 597.111 469.62 599.676 472.364 602.169C498.379 624.966 531.192 626.048 556.938 606.289C584.14 582.636 586.467 548.812 571.731 524.135C574.087 522.724 576.398 521.152 578.655 519.42C622.671 481.146 601.559 416.245 548.411 406.552C544.609 406.233 540.889 406.179 537.266 406.37C538.155 376.403 517.265 347.605 483.262 341.403C446.052 338.281 416.727 360.547 408.781 388.627C349.879 389.381 314.714 454.316 362.424 497.661C366.019 500.811 369.744 503.547 373.563 505.87C355.361 528.893 354.192 562.62 382.781 588.596C407.99 610.687 439.583 612.387 464.943 594.492Z'
                        />
                      </g>
                    </g>
                  </g>
                  <g className='ConversationBackground__Part'>
                    <g className='ConversationBackground__Part__Inner'>
                      <path
                        d='M849.356 492.356C867.959 462.798 837.069 398.776 807.643 395.301C783.861 392.494 764.683 429.667 748.28 422.261C735.839 416.642 743.877 393.901 727.959 378.586C711.762 363.005 677.926 361.998 662.178 377.509C641.662 397.721 655.656 442.235 673.41 466.476C715.826 524.401 825.88 529.661 849.356 492.356Z'
                        fill='currentColor'
                      />
                    </g>
                  </g>

                  <g className='ConversationBackground__Part'>
                    <g className='ConversationBackground__Part__Inner'>
                      <path
                        d='M393.647 628.007L381.749 626.263C375.251 625.309 368.64 626.977 363.37 630.898L353.723 638.077C348.456 641.997 344.96 647.852 344.007 654.35L342.263 666.248C341.309 672.746 342.977 679.357 346.898 684.627L354.077 694.274C357.999 699.543 363.852 703.039 370.35 703.993L382.248 705.737C388.746 706.691 395.357 705.023 400.627 701.102L410.274 693.923C415.543 690.001 419.039 684.148 419.993 677.65L421.737 665.752C422.691 659.254 421.023 652.643 417.102 647.373L409.923 637.726C406.001 632.457 400.148 628.961 393.65 628.007H393.647Z'
                        fill='currentColor'
                      />
                    </g>
                  </g>
                  <g className='ConversationBackground__Part'>
                    <g className='ConversationBackground__Part__Inner'>
                      <path
                        d='M506.516 806.572L557.52 822.317C584.634 830.684 610.633 806.542 604.318 778.858L592.442 726.782L580.566 674.706C574.251 647.022 540.362 636.563 519.562 655.874L480.434 692.205L441.306 728.541C420.506 747.853 428.398 782.459 455.512 790.831L506.516 806.577V806.572Z'
                        fill='currentColor'
                      />
                    </g>
                  </g>
                  <g className='ConversationBackground__Part' data-interactive={0}>
                    <g className='ConversationBackground__Part__Inner'>
                      <g
                        ref={(ref) => {
                          hoverRef.current[0] = ref
                        }}
                      >
                        <path
                          d='M926.569 708.203C925.415 692.016 918.813 676.525 907.765 664.411H907.769C897.905 652.96 893.568 647.885 891.765 641.321C891.059 636.694 891.237 630.974 891.511 622.039C891.511 622.028 891.511 622.017 891.511 622.007C891.511 621.923 891.514 621.851 891.518 621.768H891.514C891.776 613.767 890.699 605.831 888.39 598.275C883.008 577.976 868.865 560.139 848.154 550.675C820.666 538.12 789.398 543.884 768.297 562.847C754.528 574.533 739.307 580.153 722.635 579.707C722.635 579.707 722.61 579.707 722.599 579.707C722.519 579.707 722.442 579.703 722.362 579.7C693.909 578.925 666.381 594.731 653.728 622.021C646.165 638.334 645.2 655.983 649.722 672.018C652.588 683.49 658.243 694.214 666.301 703.138H666.297C678.135 717.018 681.966 721.572 682.978 730.648C683.269 734.7 683.138 739.696 682.963 746.625C682.963 746.636 682.963 746.647 682.963 746.658C682.963 746.741 682.96 746.813 682.956 746.896H682.96C682.909 748.757 682.931 750.615 683.022 752.468C683.502 779.4 699.087 805.017 725.333 817.167C752.763 829.863 784.056 824.255 805.252 805.397C819.079 793.778 834.329 788.234 851.001 788.765C851.001 788.765 851.027 788.765 851.038 788.765C851.118 788.765 851.194 788.769 851.274 788.773C879.724 789.689 907.332 774.02 920.12 746.791C925.993 734.291 927.974 720.982 926.565 708.203H926.569Z'
                          fill='currentColor'
                        />
                        <path
                          className={`fill-light-blue transition-opacity duration-333 ease-out-quad ${
                            choices?.length > 0 && isReady && (hoverIndex === null || hoverIndex === 0)
                              ? 'opacity-100'
                              : 'opacity-0'
                          }`}
                          d='M926.569 708.203C925.415 692.016 918.813 676.525 907.765 664.411H907.769C897.905 652.96 893.568 647.885 891.765 641.321C891.059 636.694 891.237 630.974 891.511 622.039C891.511 622.028 891.511 622.017 891.511 622.007C891.511 621.923 891.514 621.851 891.518 621.768H891.514C891.776 613.767 890.699 605.831 888.39 598.275C883.008 577.976 868.865 560.139 848.154 550.675C820.666 538.12 789.398 543.884 768.297 562.847C754.528 574.533 739.307 580.153 722.635 579.707C722.635 579.707 722.61 579.707 722.599 579.707C722.519 579.707 722.442 579.703 722.362 579.7C693.909 578.925 666.381 594.731 653.728 622.021C646.165 638.334 645.2 655.983 649.722 672.018C652.588 683.49 658.243 694.214 666.301 703.138H666.297C678.135 717.018 681.966 721.572 682.978 730.648C683.269 734.7 683.138 739.696 682.963 746.625C682.963 746.636 682.963 746.647 682.963 746.658C682.963 746.741 682.96 746.813 682.956 746.896H682.96C682.909 748.757 682.931 750.615 683.022 752.468C683.502 779.4 699.087 805.017 725.333 817.167C752.763 829.863 784.056 824.255 805.252 805.397C819.079 793.778 834.329 788.234 851.001 788.765C851.001 788.765 851.027 788.765 851.038 788.765C851.118 788.765 851.194 788.769 851.274 788.773C879.724 789.689 907.332 774.02 920.12 746.791C925.993 734.291 927.974 720.982 926.565 708.203H926.569Z'
                        />
                      </g>
                    </g>
                  </g>
                </>
              ) : (
                <>
                  <g className='ConversationBackground__Part'>
                    <g className='ConversationBackground__Part__Inner'>
                      <path
                        d='M473.721 264.657C459.639 221.918 423.212 140.927 353.709 161.201C284.441 181.402 296.517 270.221 307.378 314.75C310.818 328.856 325.415 337.132 339.279 332.885L457.134 296.751C470.666 292.603 478.159 278.122 473.721 264.662V264.657Z'
                        fill='currentColor'
                      />
                    </g>
                  </g>
                  <g className='ConversationBackground__Part'>
                    <g className='ConversationBackground__Part__Inner'>
                      <path
                        d='M698.356 212.356C716.959 182.798 686.069 118.776 656.643 115.301C632.861 112.494 613.683 149.667 597.28 142.261C584.839 136.642 592.877 113.901 576.959 98.5861C560.762 83.0053 526.926 81.9978 511.178 97.5092C490.662 117.721 504.656 162.235 522.41 186.476C564.826 244.401 674.88 249.661 698.356 212.356Z'
                        fill='currentColor'
                      />
                    </g>
                  </g>
                  <g className='ConversationBackground__Part'>
                    <g className='ConversationBackground__Part__Inner'>
                      <path
                        d='M802.956 188.038C802.434 189.33 801.913 190.619 801.391 191.91C800.546 194.581 801.203 197.11 802.116 199.604C803.66 203.822 805.462 207.935 807.09 212.118C808.177 214.912 809.425 217.629 810.642 220.356C813.022 225.688 815.726 230.865 818.65 235.921C820.134 238.487 821.709 240.988 823.444 243.39C824.677 245.097 826.131 246.523 828.079 247.382C828.953 247.735 829.826 248.088 830.699 248.441C833.89 249.623 836.896 248.739 839.862 247.559C842.674 246.439 845.262 244.865 847.921 243.432C849.798 242.419 851.714 241.481 853.625 240.538C856.679 239.028 859.724 237.505 862.785 236.009C865.024 234.915 867.277 233.844 869.5 232.718C873.796 230.542 878.233 228.633 882.334 226.078C883.797 225.165 885.161 224.123 886.424 222.943C887.214 222.207 887.562 221.192 888.141 220.323C888.6 219.187 889.058 218.052 889.518 216.916C890.595 213.983 889.66 211.242 888.73 208.506C887.783 205.719 886.645 203.009 885.316 200.387C883.839 197.475 882.362 194.562 880.903 191.64C879.851 189.536 878.841 187.409 877.805 185.298C876.063 181.742 874.412 178.145 872.684 174.582C872.08 173.335 871.639 172.011 871.047 170.757C870.041 168.632 869.075 166.49 867.635 164.595C866.734 163.408 865.616 162.575 864.302 161.95C864.132 161.869 863.916 161.864 863.816 161.664C863.045 161.353 862.277 161.042 861.506 160.731C860.428 160.543 859.429 160.018 858.305 160.001C855.855 159.967 853.535 160.58 851.27 161.439C848.362 162.542 845.638 164.038 842.93 165.557C840.997 166.641 839.009 167.621 837.047 168.646C834.176 170.146 831.282 171.604 828.402 173.086C826.235 174.201 824.061 175.302 821.894 176.417C818.868 177.977 815.833 179.522 812.832 181.136C811.053 182.093 809.274 183.057 807.467 183.962C805.574 184.912 803.876 186.036 802.954 188.045L802.956 188.038Z'
                        fill='currentColor'
                      />
                    </g>
                  </g>
                  <g className='ConversationBackground__Part' data-interactive={0}>
                    <g className='ConversationBackground__Part__Inner'>
                      <g
                        ref={(ref) => {
                          hoverRef.current[0] = ref
                        }}
                      >
                        <path
                          d='M467.943 576.492C470.15 579.111 472.62 581.676 475.364 584.169C501.379 606.966 534.192 608.048 559.938 588.289C587.14 564.636 589.467 530.812 574.731 506.135C577.087 504.724 579.398 503.152 581.655 501.42C625.671 463.146 604.559 398.245 551.411 388.552C547.609 388.233 543.889 388.179 540.266 388.37C541.155 358.403 520.265 329.605 486.262 323.403C449.052 320.281 419.727 342.547 411.781 370.627C352.879 371.381 317.714 436.316 365.424 479.661C369.019 482.811 372.744 485.547 376.563 487.87C358.361 510.893 357.192 544.62 385.781 570.596C410.99 592.687 442.583 594.387 467.943 576.492Z'
                          fill='currentColor'
                        />
                        <path
                          className={`fill-mid-blue transition-opacity duration-333 ease-out-quad ${
                            choices?.length > 0 && isReady && (hoverIndex === null || hoverIndex === 0)
                              ? 'opacity-100'
                              : 'opacity-0'
                          }`}
                          d='M467.943 576.492C470.15 579.111 472.62 581.676 475.364 584.169C501.379 606.966 534.192 608.048 559.938 588.289C587.14 564.636 589.467 530.812 574.731 506.135C577.087 504.724 579.398 503.152 581.655 501.42C625.671 463.146 604.559 398.245 551.411 388.552C547.609 388.233 543.889 388.179 540.266 388.37C541.155 358.403 520.265 329.605 486.262 323.403C449.052 320.281 419.727 342.547 411.781 370.627C352.879 371.381 317.714 436.316 365.424 479.661C369.019 482.811 372.744 485.547 376.563 487.87C358.361 510.893 357.192 544.62 385.781 570.596C410.99 592.687 442.583 594.387 467.943 576.492Z'
                        />
                      </g>
                    </g>
                  </g>
                  <g className='ConversationBackground__Part'>
                    <g className='ConversationBackground__Part__Inner'>
                      <path
                        d='M817.686 376.633C816.845 364.84 812.035 353.554 803.986 344.728H803.989C796.802 336.385 793.642 332.687 792.329 327.905C791.814 324.534 791.944 320.367 792.143 313.857C792.143 313.849 792.143 313.841 792.143 313.833C792.143 313.773 792.146 313.72 792.149 313.659H792.146C792.337 307.83 791.552 302.048 789.87 296.544C785.949 281.754 775.645 268.758 760.555 261.864C740.528 252.716 717.747 256.915 702.373 270.731C692.342 279.246 681.253 283.34 669.106 283.015C669.106 283.015 669.087 283.015 669.079 283.015C669.021 283.015 668.965 283.012 668.907 283.01C648.177 282.445 628.12 293.961 618.902 313.844C613.391 325.729 612.688 338.588 615.983 350.27C618.071 358.629 622.191 366.441 628.062 372.943H628.059C636.684 383.056 639.475 386.374 640.213 392.987C640.425 395.938 640.329 399.578 640.202 404.627C640.202 404.635 640.202 404.643 640.202 404.65C640.202 404.711 640.199 404.764 640.197 404.825H640.199C640.162 406.18 640.178 407.533 640.244 408.884C640.595 428.505 651.949 447.169 671.071 456.021C691.056 465.272 713.855 461.186 729.298 447.446C739.372 438.981 750.483 434.942 762.63 435.329C762.63 435.329 762.648 435.329 762.656 435.329C762.714 435.329 762.77 435.332 762.828 435.334C783.556 436.002 803.67 424.586 812.987 404.748C817.267 395.64 818.71 385.944 817.683 376.633H817.686Z'
                        fill='currentColor'
                      />
                    </g>
                  </g>
                  <g className='ConversationBackground__Part'>
                    <g className='ConversationBackground__Part__Inner'>
                      <path
                        d='M393.647 628.007L381.749 626.263C375.251 625.309 368.64 626.977 363.37 630.898L353.723 638.077C348.456 641.997 344.96 647.852 344.007 654.35L342.263 666.248C341.309 672.746 342.977 679.357 346.898 684.627L354.077 694.274C357.999 699.543 363.852 703.039 370.35 703.993L382.248 705.737C388.746 706.691 395.357 705.023 400.627 701.102L410.274 693.923C415.543 690.001 419.039 684.148 419.993 677.65L421.737 665.752C422.691 659.254 421.023 652.643 417.102 647.373L409.923 637.726C406.001 632.457 400.148 628.961 393.65 628.007H393.647Z'
                        fill='currentColor'
                      />
                    </g>
                  </g>
                  <g className='ConversationBackground__Part'>
                    <g className='ConversationBackground__Part__Inner'>
                      <path
                        d='M506.516 806.572L557.52 822.317C584.634 830.684 610.633 806.542 604.318 778.858L592.442 726.782L580.566 674.706C574.251 647.022 540.362 636.563 519.562 655.874L480.434 692.205L441.306 728.541C420.506 747.853 428.398 782.459 455.512 790.831L506.516 806.577V806.572Z'
                        fill='currentColor'
                      />
                    </g>
                  </g>
                  <g className='ConversationBackground__Part' data-interactive={1}>
                    <g className='ConversationBackground__Part__Inner'>
                      <g
                        ref={(ref) => {
                          hoverRef.current[1] = ref
                        }}
                      >
                        <path
                          d='M892.159 593.134C879.105 582.376 862.385 579.015 846.138 574.127C849.735 563.243 853.275 552.383 855.312 541.572C859.885 507.058 819.59 523.627 781.165 534.458C769.661 520.542 759.313 504.96 746.924 492.594C725.502 473.423 715.628 499.152 705.696 530.015C693.433 526.394 680.998 523.25 668.665 519.854C668.234 519.734 667.778 519.634 667.336 519.564C625.665 513.118 659.136 574.247 664.435 600.818C650.421 611.401 637.281 623.207 623.865 634.6C623.522 634.89 623.183 635.209 622.878 635.537C603.101 656.692 629.626 666.508 656.262 674.014C642.175 718.907 628.253 747.212 724.843 711.82C725.458 711.571 726.073 711.334 726.685 711.109C764.655 750.698 781.52 804.599 807.064 721.823C819.057 725.549 831.123 728.415 843.407 727.97C848.887 727.771 853.535 723.67 854.036 718.208C855.985 696.841 844.785 677.304 840.23 656.321C858.512 640.358 881.409 627.811 894.252 607.414C897.173 602.774 896.403 596.623 892.17 593.134H892.159Z'
                          fill='currentColor'
                        />
                        <path
                          className={`fill-coral transition-opacity duration-333 ease-out-quad ${
                            choices?.length > 0 && isReady && (hoverIndex === null || hoverIndex === 1)
                              ? 'opacity-100'
                              : 'opacity-0'
                          }`}
                          d='M892.159 593.134C879.105 582.376 862.385 579.015 846.138 574.127C849.735 563.243 853.275 552.383 855.312 541.572C859.885 507.058 819.59 523.627 781.165 534.458C769.661 520.542 759.313 504.96 746.924 492.594C725.502 473.423 715.628 499.152 705.696 530.015C693.433 526.394 680.998 523.25 668.665 519.854C668.234 519.734 667.778 519.634 667.336 519.564C625.665 513.118 659.136 574.247 664.435 600.818C650.421 611.401 637.281 623.207 623.865 634.6C623.522 634.89 623.183 635.209 622.878 635.537C603.101 656.692 629.626 666.508 656.262 674.014C642.175 718.907 628.253 747.212 724.843 711.82C725.458 711.571 726.073 711.334 726.685 711.109C764.655 750.698 781.52 804.599 807.064 721.823C819.057 725.549 831.123 728.415 843.407 727.97C848.887 727.771 853.535 723.67 854.036 718.208C855.985 696.841 844.785 677.304 840.23 656.321C858.512 640.358 881.409 627.811 894.252 607.414C897.173 602.774 896.403 596.623 892.17 593.134H892.159Z'
                        />
                      </g>
                    </g>
                  </g>
                </>
              )}
            </svg>

            {choices?.length && (
              <div className='absolute left-0 right-0 top-auto bottom-0 md:inset-0 text-primary flex flex-col items-end justify-center select-none z-2'>
                <div className='Choices flex items-end justify-center gap-24 py-40 md:py-24'>
                  {choices &&
                    choices.map((choice, index) => (
                      <ConversationBackgroundItem
                        key={`${subject}-${index}`}
                        choice={choice}
                        index={index}
                        hoverIndex={hoverIndex}
                        isReady={isReady}
                        subject={subject}
                        position={getChoicePosition(index)}
                        setHoverIndex={setHoverIndex}
                        onChoose={onChoose}
                      />
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )
    )
  }
)

export default ConversationBackground
